'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';

export default function ForgotPasswordPage() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-navy flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue/3 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8 gap-3">
          <AppLogo size={44} text="Pretium+" />
          <p className="text-silver text-sm text-center mt-1">Motor Insurance MGA Platform</p>
        </div>

        <div className="glass-card rounded-2xl p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue/10 border border-blue/30 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <h2 className="font-display text-2xl font-bold text-white mb-2">Check your email</h2>
              <p className="text-silver text-sm mb-6 leading-relaxed">
                We sent a password reset link to <span className="text-white font-semibold">{email}</span>. Click the link in the email to reset your password.
              </p>
              <p className="text-silver-dim text-xs mb-6">
                Did not receive it? Check your spam folder or{' '}
                <button
                  onClick={() => setSent(false)}
                  className="text-blue hover:text-blue/80 transition-colors font-semibold"
                >
                  try again
                </button>
                .
              </p>
              <Link
                href="/login"
                className="btn-electric w-full py-3 rounded-lg text-sm font-semibold tracking-wide uppercase inline-flex items-center justify-center"
              >
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <Link
                  href="/login"
                  className="flex items-center justify-center w-8 h-8 rounded-lg border border-silver-dim hover:border-silver hover:bg-navy-light transition-all duration-200 text-silver hover:text-white"
                  aria-label="Back to login"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                </Link>
                <span className="text-silver text-sm">Back to sign in</span>
              </div>

              <div className="w-12 h-12 rounded-xl bg-blue/10 border border-blue/30 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </div>

              <h1 className="font-display text-2xl font-bold text-white mb-1">Reset password</h1>
              <p className="text-silver text-sm mb-6 leading-relaxed">
                Enter the email address associated with your account and we will send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-silver">Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@pretiumplus.com"
                    autoComplete="email"
                    className="w-full bg-navy-mid border border-silver-dim rounded-lg px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue/40 transition-all duration-200"
                    required
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-red-alert/10 border border-red-alert/30">
                    <svg className="w-4 h-4 text-red-alert flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <p className="text-red-alert text-xs leading-relaxed">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-electric w-full py-3 rounded-lg text-sm font-semibold tracking-wide uppercase mt-1 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-xs text-silver-dim mt-6">
          © {new Date().getFullYear()} Pretium+ — Confidential Internal Platform
        </p>
      </div>
    </main>
  );
}
